import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { T, SECTIONS_ORDER } from "../i18n/strings";
import { Trash2, Sparkles, Languages, Upload, PlusCircle, Edit3, X } from "lucide-react";

const empty = {
  id: null,
  section: "brazil-us",
  title_pt: "", title_en: "",
  dek_pt: "", dek_en: "",
  body_pt: "", body_en: "",
  summary_pt: "", summary_en: "",
  hero_image_url: "", hero_image_credit: "",
  author_name: "Editorial Board",
  author_bio_pt: "", author_bio_en: "",
  tags: [], is_featured: false, is_lead: false, published: true,
};

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const { lang } = useLanguage();
  const nav = useNavigate();
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) nav("/login", { replace: true });
  }, [authLoading, user, nav]);

  const refresh = () => api.get("/admin/articles").then((r) => setArticles(r.data));
  useEffect(() => { if (user) refresh(); }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const payload = { ...editing, tags: (editing.tags || []).filter(Boolean) };
      if (editing.id) {
        await api.put(`/articles/${editing.id}`, payload);
      } else {
        await api.post("/articles", payload);
      }
      setEditing(null);
      await refresh();
      setMsg(lang === "pt" ? "Salvo com sucesso." : "Saved successfully.");
    } catch (err) {
      setMsg(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "pt" ? "Excluir?" : "Delete?")) return;
    await api.delete(`/articles/${id}`);
    await refresh();
  };

  const translate = async (fromKey, toKey, sourceLang, targetLang) => {
    if (!editing?.[fromKey]) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/translate", { text: editing[fromKey], source: sourceLang, target: targetLang });
      setEditing((e) => ({ ...e, [toKey]: data.translation }));
    } catch (err) {
      setMsg(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const summarize = async (bodyKey, sumKey, language) => {
    if (!editing?.[bodyKey]) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/summarize", { text: editing[bodyKey], language });
      setEditing((e) => ({ ...e, [sumKey]: data.summary }));
    } catch (err) {
      setMsg(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setEditing((e) => ({ ...e, hero_image_url: url }));
    } catch (err) {
      setMsg(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (authLoading) return <p className="max-w-5xl mx-auto py-16 text-center font-mono-meta text-xs uppercase tracking-widest">Loading...</p>;
  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-14" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8 border-b border-[#081422]/15 pb-4">
        <div>
          <div className="pw-eyebrow">{T.labels.editorial[lang]}</div>
          <h1 className="font-serif-display text-4xl font-bold text-[#081422] mt-1">
            {lang === "pt" ? "Painel Editorial" : "Editorial Desk"}
          </h1>
          <p className="font-sans-ui text-sm text-[#64748B] mt-1">{user.email}</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="pw-btn" data-testid="new-article-btn">
          <PlusCircle size={16} /> {T.labels.newArticle[lang]}
        </button>
      </div>

      {msg && <div className="mb-4 p-3 bg-[#F7F2E6] font-mono-meta text-xs uppercase tracking-widest text-[#0F2137]" data-testid="admin-msg">{msg}</div>}

      {editing ? (
        <form onSubmit={save} className="pw-window-frame p-6 lg:p-8 space-y-5" data-testid="article-editor">
          <div className="flex justify-between items-center">
            <h2 className="font-serif-display text-2xl font-semibold text-[#081422]">
              {editing.id ? T.labels.edit[lang] : T.labels.newArticle[lang]}
            </h2>
            <button type="button" onClick={() => setEditing(null)} className="p-2 hover:bg-[#F7F2E6]" data-testid="close-editor"><X size={18} /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="pw-eyebrow block mb-2">{T.labels.section[lang]}</label>
              <select value={editing.section} onChange={(e) => setEditing({ ...editing, section: e.target.value })} className="pw-input" data-testid="editor-section">
                {SECTIONS_ORDER.map((s) => <option key={s} value={s}>{T.nav[s].en}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 self-end pb-2"><input type="checkbox" checked={editing.is_lead} onChange={(e) => setEditing({ ...editing, is_lead: e.target.checked })} data-testid="editor-lead" /> Lead</label>
            <label className="flex items-center gap-2 self-end pb-2"><input type="checkbox" checked={editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} data-testid="editor-featured" /> Featured</label>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="pw-eyebrow">PT — Português</h3>
              <input placeholder="Título" value={editing.title_pt} onChange={(e) => setEditing({ ...editing, title_pt: e.target.value })} className="pw-input font-serif-display" data-testid="editor-title-pt" />
              <input placeholder="Subtítulo" value={editing.dek_pt} onChange={(e) => setEditing({ ...editing, dek_pt: e.target.value })} className="pw-input" data-testid="editor-dek-pt" />
              <textarea placeholder="Corpo (PT)" value={editing.body_pt} onChange={(e) => setEditing({ ...editing, body_pt: e.target.value })} className="pw-input pw-textarea min-h-[200px]" data-testid="editor-body-pt" />
              <textarea placeholder="Resumo (opcional)" value={editing.summary_pt} onChange={(e) => setEditing({ ...editing, summary_pt: e.target.value })} className="pw-input" data-testid="editor-summary-pt" />
              <div className="flex gap-2">
                <button type="button" onClick={() => translate("title_en", "title_pt", "en", "pt")} disabled={busy} className="pw-btn-outline pw-btn text-xs" data-testid="translate-title-to-pt"><Languages size={14} /> EN→PT título</button>
                <button type="button" onClick={() => translate("body_en", "body_pt", "en", "pt")} disabled={busy} className="pw-btn-outline pw-btn text-xs" data-testid="translate-body-to-pt"><Languages size={14} /> EN→PT corpo</button>
              </div>
              <button type="button" onClick={() => summarize("body_pt", "summary_pt", "pt")} disabled={busy} className="pw-btn-outline pw-btn text-xs" data-testid="summarize-pt"><Sparkles size={14} /> Resumo IA</button>
            </div>
            <div className="space-y-3">
              <h3 className="pw-eyebrow">EN — English</h3>
              <input placeholder="Title" value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} className="pw-input font-serif-display" data-testid="editor-title-en" />
              <input placeholder="Deck" value={editing.dek_en} onChange={(e) => setEditing({ ...editing, dek_en: e.target.value })} className="pw-input" data-testid="editor-dek-en" />
              <textarea placeholder="Body (EN)" value={editing.body_en} onChange={(e) => setEditing({ ...editing, body_en: e.target.value })} className="pw-input pw-textarea min-h-[200px]" data-testid="editor-body-en" />
              <textarea placeholder="Summary (optional)" value={editing.summary_en} onChange={(e) => setEditing({ ...editing, summary_en: e.target.value })} className="pw-input" data-testid="editor-summary-en" />
              <div className="flex gap-2">
                <button type="button" onClick={() => translate("title_pt", "title_en", "pt", "en")} disabled={busy} className="pw-btn-outline pw-btn text-xs" data-testid="translate-title-to-en"><Languages size={14} /> PT→EN title</button>
                <button type="button" onClick={() => translate("body_pt", "body_en", "pt", "en")} disabled={busy} className="pw-btn-outline pw-btn text-xs" data-testid="translate-body-to-en"><Languages size={14} /> PT→EN body</button>
              </div>
              <button type="button" onClick={() => summarize("body_en", "summary_en", "en")} disabled={busy} className="pw-btn-outline pw-btn text-xs" data-testid="summarize-en"><Sparkles size={14} /> AI Summary</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="pw-eyebrow block mb-2">Hero image URL</label>
              <input value={editing.hero_image_url} onChange={(e) => setEditing({ ...editing, hero_image_url: e.target.value })} className="pw-input" data-testid="editor-hero-url" />
            </div>
            <div>
              <label className="pw-eyebrow block mb-2">
                <Upload size={14} className="inline mr-1" /> {T.labels.uploadImage[lang]}
              </label>
              <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} className="pw-input" data-testid="editor-upload" />
            </div>
          </div>
          {editing.hero_image_url && <img src={editing.hero_image_url} alt="hero" className="w-full max-h-64 object-cover pw-window-frame p-0" />}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input placeholder="Author name" value={editing.author_name} onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} className="pw-input" data-testid="editor-author" />
            <input placeholder="Tags (comma separated)" value={(editing.tags || []).join(", ")} onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map(s => s.trim()) })} className="pw-input" data-testid="editor-tags" />
            <input placeholder="Image credit" value={editing.hero_image_credit} onChange={(e) => setEditing({ ...editing, hero_image_credit: e.target.value })} className="pw-input" data-testid="editor-credit" />
          </div>

          <div className="flex gap-3">
            <button type="submit" disabled={busy} className="pw-btn" data-testid="save-article">{busy ? "..." : T.labels.publish[lang]}</button>
            <button type="button" onClick={() => setEditing(null)} className="pw-btn-outline pw-btn" data-testid="cancel-article">Cancel</button>
          </div>
        </form>
      ) : (
        <div className="space-y-2" data-testid="articles-list">
          {articles.map((a) => (
            <div key={a.id} className="pw-window-frame p-4 flex items-center gap-4">
              <img src={a.hero_image_url} alt="" className="w-16 h-16 object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="pw-eyebrow text-[0.65rem]">{T.nav[a.section]?.en}</div>
                <h3 className="font-serif-display text-lg font-semibold text-[#081422] truncate">{a.title_en}</h3>
                <p className="font-sans-ui text-xs text-[#64748B]">{new Date(a.published_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => setEditing(a)} className="p-2 hover:bg-[#F7F2E6]" data-testid={`edit-${a.id}`}><Edit3 size={16} /></button>
              <button onClick={() => del(a.id)} className="p-2 hover:bg-[#F7F2E6] text-[#8B0000]" data-testid={`del-${a.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
          {articles.length === 0 && <p className="font-serif-body text-[#475569]">No articles yet.</p>}
        </div>
      )}
    </div>
  );
}
